import React, { useEffect, useCallback, useRef, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AudioTrack } from '../../hooks/useWebAudio';
import { useAudioTimeline } from '../../hooks/useAudioTimeline';
import { TimelineRuler } from './TimelineRuler';
import { TrackLane } from './TrackLane';
import { Icon } from '../common/Icon';
import { FaSearchPlus, FaSearchMinus, FaExpand, FaLock, FaUnlock } from 'react-icons/fa';

const TimelineContainer = styled.div`
  background: #1a1a1a;
  border-radius: 8px;
  border: 1px solid #333;
  overflow: hidden;
  width: 100%;
  max-width: calc(100vw - 400px);
  min-width: 600px;

  @media (max-width: 768px) {
    max-width: 80vw;
  }
`;

const TimelineHeader = styled.div`
  background: #2a2a2a;
  border-bottom: 1px solid #333;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const TimelineTitle = styled.h3`
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

const TimelineControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ControlButton = styled.button<{ $active?: boolean }>`
  background: ${props => props.$active ? '#4caf50' : '#333'};
  color: white;
  border: none;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$active ? '#45a049' : '#444'};
  }
`;

const TimelineContent = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
 
`;

const TimelineViewport = styled.div`
  position: relative;
  overflow-x: scroll;
  overflow-y: auto;
  background: #111;
  width: 100%;
  max-height: 600px;
  
 
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #2a2a2a;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #666;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #888;
  }
`;

const TracksContainer = styled.div`
  min-height: 200px;
  position: relative;

`;

const GridLines = styled.div<{ $show: boolean; $pixelsPerSecond: number; $gridSize: number; $timelineWidth: number }>`
  position: absolute;
  top: 0;
  left: 0;
  margin-left: ${props => 185 -props.$pixelsPerSecond}px;
  right: 0;
  bottom: 0;
  pointer-events: none;
  opacity: ${props => props.$show ? 0.3 : 0};
  transition: opacity 0.2s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: ${props => props.$timelineWidth}px;
    background-image: repeating-linear-gradient(
      to right,
      transparent,
      transparent ${props => props.$pixelsPerSecond * props.$gridSize - 1}px,
      #4caf50 ${props => props.$pixelsPerSecond * props.$gridSize - 1}px,
      #4caf50 ${props => props.$pixelsPerSecond * props.$gridSize}px
    );
  }
`;

const RulerWrapper = styled.div`
  display: flex;
`;

const RulerSpacer = styled.div`
  width: 185px;
  min-width: 185px;
  height: 40px;
  background: #2a2a2a;
  border-right: 1px solid #333;
  border-bottom: 1px solid #444;
  padding: 8px; /* Match TrackHeader padding */
  box-sizing: border-box;
`;

const RulerContent = styled.div`
  flex: 1;
  position: relative;
  height: 40px;
  padding-left: 1px; /* Account for border between spacer and content */
`;

const EmptyTimeline = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #888;
  text-align: center;
`;


interface AudioTimelineProps {
  tracks: AudioTrack[];
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  onUpdateTrack: (trackId: string, updates: Partial<AudioTrack>) => void;
  onRemoveTrack: (trackId: string) => void;
  onDuplicateTrack?: (trackId: string) => void;
  onLoopTrack?: (trackId: string) => void;
  onSeek: (time: number) => void;
  scrollToStart?: boolean;
  className?: string;
}

const AudioTimeline: React.FC<AudioTimelineProps> = ({
  tracks,
  currentTime,
  duration,
  isPlaying,
  onUpdateTrack,
  onRemoveTrack,
  onDuplicateTrack,
  onLoopTrack,
  onSeek,
  scrollToStart,
  className
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [visibleTimeRange, setVisibleTimeRange] = useState({ start: 0, end: 10 });
  
  // Handle scroll to start when tracks are loaded
  useEffect(() => {
    if (scrollToStart && viewportRef.current) {
      console.log('Scrolling timeline to start');
      viewportRef.current.scrollLeft = 0;
      viewportRef.current.scrollTop = 0;
    }
  }, [scrollToStart]);
  
  // Force layout recalculation when tracks change to ensure scrollbars appear
  useEffect(() => {
    if (viewportRef.current && tracks.length > 0) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        if (viewportRef.current) {
          // Force browser to recalculate layout
          viewportRef.current.style.height = 'auto';
          // Trigger reflow
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          viewportRef.current.offsetHeight;
          viewportRef.current.style.height = '';
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [tracks.length]);
  
  const {
    viewport,
    isAutoScroll,
    snapToGrid,
    gridSize,
    isDragging,
    dragTrackId,
    setViewport,
    setIsAutoScroll,
    setSnapToGrid,
    zoomIn,
    zoomOut,
    zoomToFit,
    snapTimeToGrid,
    startDrag,
    updateDrag,
    endDrag
  } = useAudioTimeline(800, duration);


  // Auto-scroll calculation (memoized to prevent recalculation)
  const handleAutoScroll = useCallback(() => {
    if (!isPlaying || !isAutoScroll || !viewportRef.current) return;
    
    const playheadPosition = 185 + currentTime * viewport.pixelsPerSecond; // 185px offset for header
    const viewportWidth = viewportRef.current.clientWidth;
    const scrollLeft = viewportRef.current.scrollLeft;
    
    // Scroll if playhead is approaching the right edge
    if (playheadPosition > scrollLeft + viewportWidth * 0.8) {
      viewportRef.current.scrollLeft = playheadPosition - viewportWidth * 0.2;
    }
    // Scroll if playhead is behind the left edge
    else if (playheadPosition < scrollLeft) {
      viewportRef.current.scrollLeft = Math.max(0, playheadPosition - viewportWidth * 0.2);
    }
  }, [isPlaying, isAutoScroll, currentTime, viewport.pixelsPerSecond]);

  // Auto-scroll to current time when playing (throttled)
  useEffect(() => {
    handleAutoScroll();
  }, [handleAutoScroll]);

  // Update viewport when duration changes
  useEffect(() => {
    if (duration > 0) {
      setViewport({ endTime: Math.max(viewport.endTime, duration) });
    }
  }, [duration, setViewport, viewport.endTime]);

  // Scroll to start when requested
  useEffect(() => {
    if (scrollToStart && viewportRef.current) {
      viewportRef.current.scrollLeft = 0;
    }
  }, [scrollToStart]);

  const handleSeek = useCallback((time: number) => {
    const snappedTime = snapTimeToGrid(time);
    onSeek(snappedTime);
  }, [onSeek, snapTimeToGrid]);

  // Memoize timeline width calculation to prevent recalculation on every render
  const timelineWidth = useMemo(() => {
    return Math.max(800, (duration * viewport.pixelsPerSecond) + 50);
  }, [duration, viewport.pixelsPerSecond]);

  // Update visible time range based on scroll position
  const handleScroll = useCallback(() => {
    if (!viewportRef.current) return;
    
    const scrollLeft = viewportRef.current.scrollLeft;
    const viewportWidth = viewportRef.current.clientWidth;
    
    // Account for the 185px header offset
    const startTime = Math.max(0, (scrollLeft - 185) / viewport.pixelsPerSecond);
    const endTime = Math.min(duration, (scrollLeft - 185 + viewportWidth) / viewport.pixelsPerSecond);
    
    setVisibleTimeRange({ start: startTime, end: endTime });
  }, [viewport.pixelsPerSecond, duration]);

  // Update visible range on scroll
  useEffect(() => {
    const viewportEl = viewportRef.current;
    if (!viewportEl) return;
    
    handleScroll(); // Initial calculation
    viewportEl.addEventListener('scroll', handleScroll);
    
    return () => viewportEl.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Recalculate visible range when pixelsPerSecond changes
  useEffect(() => {
    handleScroll();
  }, [viewport.pixelsPerSecond, handleScroll]);

  return (
    <TimelineContainer className={className}>
      <TimelineHeader>
        <TimelineTitle>Meditation Audio Mixer</TimelineTitle>
        
        <TimelineControls>
          <ControlButton onClick={zoomIn}>
            <Icon icon={FaSearchPlus} />
          </ControlButton>
          
          <ControlButton onClick={zoomOut}>
            <Icon icon={FaSearchMinus} />
          </ControlButton>
          
          <ControlButton onClick={() => zoomToFit(duration)}>
            <Icon icon={FaExpand} />
          </ControlButton>
          
          <ControlButton 
            $active={isAutoScroll}
            onClick={() => setIsAutoScroll(!isAutoScroll)}
          >
            <Icon icon={isAutoScroll ? FaLock : FaUnlock} />
            Auto-scroll
          </ControlButton>
          
          <ControlButton 
            $active={snapToGrid}
            onClick={() => setSnapToGrid(!snapToGrid)}
          >
            Grid
          </ControlButton>
        </TimelineControls>
      </TimelineHeader>

      <TimelineContent>
        <TimelineViewport ref={viewportRef}>
          <div style={{ width: timelineWidth + 185, minWidth: '100%' }}>
            <RulerWrapper>
              <RulerSpacer />
              <RulerContent>
                <TimelineRuler
                  width={timelineWidth}
                  duration={duration}
                  currentTime={currentTime}
                  pixelsPerSecond={viewport.pixelsPerSecond}
                  onSeek={handleSeek}
                  viewportRef={viewportRef}
                />
              </RulerContent>
            </RulerWrapper>
            
            <TracksContainer>
              <GridLines 
                $show={isDragging && snapToGrid}
                $pixelsPerSecond={viewport.pixelsPerSecond}
                $gridSize={gridSize}
                $timelineWidth={timelineWidth}
              />
              {tracks.length === 0 ? (
                <EmptyTimeline>
                  <div>
                    <p>No audio tracks loaded</p>
                    <p>Add tracks from the library below or generate brainwave audio</p>
                  </div>
                </EmptyTimeline>
              ) : (
                tracks.map(track => (
                  <TrackLane
                    key={track.id}
                    track={track}
                    pixelsPerSecond={viewport.pixelsPerSecond}
                    visibleStartTime={visibleTimeRange.start}
                    visibleEndTime={visibleTimeRange.end}
                    onUpdateTrack={onUpdateTrack}
                    onRemoveTrack={onRemoveTrack}
                    onDuplicateTrack={onDuplicateTrack}
                    onLoopTrack={onLoopTrack}
                    viewportRef={viewportRef}
                    onDragStart={startDrag}
                    onDragUpdate={updateDrag}
                    onDragEnd={endDrag}
                    isDragging={isDragging}
                    dragTrackId={dragTrackId}
                    isPlaying={isPlaying}
                    currentTime={currentTime}
                  />
                ))
              )}
            </TracksContainer>
          </div>
        </TimelineViewport>
      </TimelineContent>

    </TimelineContainer>
  );
};

export default AudioTimeline;